/**
 * Role Helpers for BookINK RBAC System
 * 
 * Role definitions:
 * - 'studio' = Studio Owner (Admin) - Full access
 * - 'staff' = Staff Artist (User) - Limited access
 * - 'superadmin' = Super Admin - God mode
 */

// Role constants
export const ROLES = {
    OWNER: 'studio',
    STAFF: 'staff',
    SUPERADMIN: 'superadmin'
};

/**
 * Check if user is an Owner or Super Admin (full access)
 */
export const isOwnerOrAdmin = (user) => {
    if (!user?.role) return false;
    return ['studio', 'superadmin'].includes(user.role);
};

/**
 * Check if user is a Staff Artist (limited access)
 */
export const isStaffArtist = (user) => {
    return user?.role === 'staff';
};

/**
 * Check if user is a Super Admin
 */
export const isSuperAdmin = (user) => {
    return user?.role === 'superadmin';
};

/**
 * Get the artist name for filtering purposes
 * Staff artists see only their own data based on this name
 */
export const getArtistName = (user) => {
    return user?.name || user?.display_name || user?.email?.split('@')[0] || 'Unknown';
};

/**
 * Filter clients by artist (for staff artists)
 * Owners see all clients, staff see only their assigned clients
 */
export const filterClientsByRole = (clients, user) => {
    if (isOwnerOrAdmin(user)) return clients;

    const artistName = getArtistName(user);
    return clients.filter(c =>
        c.staff_name === artistName ||
        c.staff_name?.toLowerCase() === artistName.toLowerCase()
    );
};

/**
 * Filter consent forms by artist (for staff artists)
 */
export const filterFormsByRole = (forms, user) => {
    if (isOwnerOrAdmin(user)) return forms;

    const artistName = getArtistName(user);
    return forms.filter(f =>
        f.staff_name === artistName ||
        f.staff_name?.toLowerCase() === artistName.toLowerCase()
    );
};

/**
 * Restricted pages that staff artists cannot access
 */
export const RESTRICTED_PAGES = ['customers', 'staff', 'marketing']; // Settings is now handled internally with RBAC

/**
 * Check if a page is restricted for the current user
 */
export const isPageRestricted = (page, user) => {
    if (isOwnerOrAdmin(user)) return false;
    return RESTRICTED_PAGES.includes(page);
};
